import { LitElement, html, svg, css, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { OxPopup } from '@operato/popup'

import { Mapping } from './types.js'
import { dataMapperContext, DataMapperContextType } from './context/data-mapper-context.js'
import './components/function-box-popup.js'

@customElement('ox-mapping-area')
export class OxMappingArea extends LitElement {
  static styles = css`
    :host {
      display: flex;

      --line-color: #5b5858;
      --selected-line-color: red;
    }

    .mapping-area {
      flex: 1;
      padding: 0;
      margin: 0;
      text-align: center;
      position: relative;
    }

    svg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }

    .mapping-box {
      fill: rgba(0, 255, 0, 0.3);
      stroke: green;
      stroke-width: 1;
      cursor: grab;
      pointer-events: all;
      user-select: none;
    }

    .mapping-box:hover {
      fill: rgba(0, 255, 0, 0.5);
    }

    .mapping-box[selected] {
      stroke: var(--selected-line-color);
      stroke-width: 2;
    }

    .mapping-line {
      stroke: var(--line-color);
      stroke-width: 1;
      fill: none;
      cursor: pointer;
      pointer-events: all;
    }

    .mapping-line[selected] {
      stroke: var(--selected-line-color);
      stroke-width: 1.5;
    }

    .mapping-line-for-select {
      stroke: transparent;
      stroke-width: 10;
      fill: none;
      cursor: pointer;
      pointer-events: all;
    }

    marker * {
      stroke: var(--line-color);
      fill: var(--line-color);
    }

    marker#arrowhead-selected * {
      stroke: var(--selected-line-color);
      fill: var(--selected-line-color);
    }
  `

  @property({ type: Array }) mappings: Mapping[] = []

  @state() selectedMapping?: Mapping
  @state() selectedLine?: { mapping: Mapping; nodeId: string; isInput: boolean }
  @state() popupVisible: boolean = false
  @state() popupPosition = { x: 0, y: 0 }

  @consume({ context: dataMapperContext, subscribe: true })
  private dataMapperContext!: DataMapperContextType

  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('keydown', this.handleKeyDown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleKeyDown)
  }

  render() {
    const mappings = this.mappings || []

    return html`
      <div class="mapping-area" @dragover=${this.handleDragOver} @drop=${this.handleDrop}>
        <svg>${mappings.map(m => this.renderMapping(m))}</svg>
      </div>
    `
  }

  renderMapping(mapping: Mapping) {
    const mappingAreaWidth = this.clientWidth
    const x = mappingAreaWidth / 2 - 50
    let y = mapping.position.y

    if (y === undefined) {
      const positions = [...mapping.inputs, ...mapping.outputs]
        .map(nodeId => this.dataMapperContext.getNodePosition(nodeId, mapping.inputs.includes(nodeId)))
        .filter(Boolean) as { x: number; y: number }[]

      y =
        positions.length > 0
          ? positions.reduce((sum, pos) => sum + pos.y, 0) / positions.length - 10
          : this.clientHeight / 2 - 10
    }

    return svg`
      <defs>
        <!-- 기본 화살표 (검은색) -->
        <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="5.4" refY="2" orient="auto" markerUnits="strokeWidth">
          <polygon points="0 0, 6 2, 0 4" />
        </marker>

        <!-- 선택된 화살표 (붉은색) -->
        <marker id="arrowhead-selected" markerWidth="6" markerHeight="4" refX="5.4" refY="2" orient="auto" markerUnits="strokeWidth">
          <polygon points="0 0, 6 2, 0 4" />
        </marker>
      </defs>
      
      <g>
        ${mapping.inputs.map(input => {
          const sourcePos = this.dataMapperContext.getNodePosition(input, true)
          const isSelected = this.isLineSelected(mapping, input, true)
          return sourcePos
            ? svg`
              <!-- 베지어 커브 경로 -->
              <path 
                d="M ${sourcePos.x},${sourcePos.y} 
                   C ${sourcePos.x + 100},${sourcePos.y} 
                     ${x - 100},${y + 10} 
                     ${x},${y + 10}"
                class="mapping-line"
                marker-end="url(#${isSelected ? 'arrowhead-selected' : 'arrowhead'})"
                ?selected=${isSelected}>
              </path>
  
              <!-- 클릭 이벤트를 위한 히트박스 -->
              <path d="M ${sourcePos.x},${sourcePos.y} 
                       C ${sourcePos.x + 100},${sourcePos.y} 
                         ${x - 100},${y + 10} 
                         ${x},${y + 10}"
                class="mapping-line-for-select"
                @click=${() => this.selectMappingLine(mapping, input, true)}>
              </path>
            `
            : nothing
        })}
  
        <rect class="mapping-box"
          width="100" height="20"
          x=${x} y=${y}
          ?selected=${this.selectedMapping?.id === mapping.id}
          @pointerdown=${(e: PointerEvent) => this.startDragForMappingBox(e, mapping)}
          @click=${() => this.selectMappingBox(mapping)}
          @dblclick=${(e: PointerEvent) => this.openPopup(e, mapping)}>
        </rect>
  
        <text x=${x + 50} y=${y + 15} fill="black" font-size="12" text-anchor="middle" pointer-events="none">
          ${mapping.mappingFunction || 'passthrough'}
        </text>
  
        ${mapping.outputs.map(output => {
          const targetPos = this.dataMapperContext.getNodePosition(output, false)
          const isSelected = this.isLineSelected(mapping, output, false)
          return targetPos
            ? svg`
              <!-- 베지어 커브 경로 -->
              <path 
                d="M ${x + 100},${y + 10} 
                   C ${x + 200},${y + 10} 
                     ${targetPos.x - 100},${targetPos.y} 
                     ${targetPos.x},${targetPos.y}"
                class="mapping-line"
                marker-end="url(#${isSelected ? 'arrowhead-selected' : 'arrowhead'})"
                ?selected=${isSelected}>
              </path>
  
              <!-- 클릭 이벤트를 위한 히트박스 -->
              <path d="M ${x + 100},${y + 10} 
                       C ${x + 200},${y + 10} 
                         ${targetPos.x - 100},${targetPos.y} 
                         ${targetPos.x},${targetPos.y}"
                class="mapping-line-for-select"
                @click=${() => this.selectMappingLine(mapping, output, false)}>
              </path>
            `
            : nothing
        })}
      </g>
    `
  }

  startDragForMappingBox(e: PointerEvent, mapping: Mapping) {
    e.stopPropagation()

    let dragStartY = mapping.position.y
    let dragOffsetY = e.clientY
    let draggingBox = mapping

    const onDrag = (e: PointerEvent) => {
      e.preventDefault()
      if (!draggingBox) return

      const newY = dragStartY + e.clientY - dragOffsetY

      const minY = 0
      const maxY = this.clientHeight - 20

      draggingBox.position = {
        x: this.clientWidth / 2 - 50,
        y: Math.max(minY, Math.min(maxY, newY))
      }

      this.requestUpdate()
    }

    const stopDrag = (e: PointerEvent) => {
      e.stopPropagation()
      window.removeEventListener('pointermove', onDrag)
      window.removeEventListener('pointerup', stopDrag)

      this.dataMapperContext.updateMappings([...this.mappings])
    }

    window.addEventListener('pointermove', onDrag)
    window.addEventListener('pointerup', stopDrag)
  }

  handleDragOver(e: DragEvent) {
    e.preventDefault()
  }

  handleDrop(e: DragEvent) {
    e.preventDefault()
    const nodeId = e.dataTransfer!.getData('application/node-id')
    if (!nodeId) {
      return
    }

    const from = e.dataTransfer!.getData('application/from')
    const { top, left } = this.getBoundingClientRect()
    const dropX = e.clientX - left
    const dropY = e.clientY - top

    let droppedOnExistingBox = false
    let selectedMapping = null

    const mappings = this.mappings.map(mapping => {
      const boxY = mapping.position.y
      const boxX = this.clientWidth / 2 - 50

      if (dropY >= boxY && dropY <= boxY + 20 && dropX >= boxX && dropX < boxX + 100) {
        droppedOnExistingBox = true

        const updatedMapping = {
          ...mapping,
          inputs: from === 'source' ? [...new Set([...mapping.inputs, nodeId])] : mapping.inputs,
          outputs: from === 'source' ? mapping.outputs : [...new Set([...mapping.outputs, nodeId])]
        }

        selectedMapping = updatedMapping

        return updatedMapping
      }

      return mapping
    })

    if (!droppedOnExistingBox) {
      const createdMapping = {
        id: this.dataMapperContext.getUniqueId(),
        function: 'Transform',
        inputs: from == 'source' ? [nodeId] : [],
        outputs: from == 'source' ? [] : [nodeId],
        position: { x: this.clientWidth / 2 - 50, y: dropY - 10 }
      }

      mappings.push(createdMapping)

      selectedMapping = createdMapping
    }

    this.dataMapperContext.updateMappings(mappings)

    selectedMapping && this.selectMappingBox(selectedMapping)
  }

  selectMappingBox(mapping: Mapping) {
    this.selectedMapping = mapping
    this.selectedLine = undefined
    this.requestUpdate()
  }

  selectMappingLine(mapping: Mapping, nodeId: string, isInput: boolean) {
    this.selectedMapping = undefined
    this.selectedLine = { mapping, nodeId, isInput }
    this.requestUpdate()
  }

  isLineSelected(mapping: Mapping, nodeId: string, isInput: boolean) {
    return (
      this.selectedLine?.mapping === mapping &&
      this.selectedLine.nodeId === nodeId &&
      this.selectedLine.isInput === isInput
    )
  }

  openPopup(e: PointerEvent, mapping: Mapping) {
    const popup = OxPopup.open({
      template: html`
        <function-box-popup
          .mapping=${mapping}
          @change=${(e: CustomEvent) => {
            const changed = e.detail

            this.dataMapperContext.updateMappings(
              this.mappings.map(mapping => {
                if (mapping.id !== changed.id) {
                  return mapping
                }

                return changed
              })
            )
            popup.close()
          }}
        ></function-box-popup>
      `,
      style: 'width: 80vw; height: 80vh;',
      preventCloseOnBlur: true,
      backdrop: true
    })
  }

  getSelectedTransformLogic(): string {
    const mapping = this.mappings.find(m => m === this.selectedMapping)
    return mapping?.mappingFunction || 'passthrough'
  }

  updateTransformLogic(e: Event) {
    const input = e.target as HTMLInputElement
    const mappings = this.mappings.map(m =>
      m.mappingFunction === this.selectedMapping ? { ...m, mappingFunction: input.value } : m
    )

    this.dataMapperContext.updateMappings(mappings)
  }

  handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.selectedLine) {
        this.deleteSelectedLine()
      } else if (this.selectedMapping) {
        this.deleteSelectedMapping()
      }
    }
  }

  deleteSelectedLine() {
    if (!this.selectedLine) return

    const { mapping, nodeId, isInput } = this.selectedLine
    const updatedMappings = this.mappings
      .map(m =>
        m === mapping
          ? {
              ...m,
              inputs: isInput ? m.inputs.filter(id => id !== nodeId) : m.inputs,
              outputs: !isInput ? m.outputs.filter(id => id !== nodeId) : m.outputs
            }
          : m
      )
      .filter(m => m.inputs.length > 0 || m.outputs.length > 0) // 입력/출력이 모두 삭제되면 제거

    this.dataMapperContext.updateMappings(updatedMappings)
    this.selectedLine = undefined
    this.requestUpdate()
  }

  deleteSelectedMapping() {
    this.dataMapperContext.updateMappings(this.mappings.filter(m => m !== this.selectedMapping))
    this.selectedMapping = undefined
    this.requestUpdate()
  }

  private handleResize = () => {
    this.requestUpdate()
  }
}
